import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractApiError } from "../lib/api";
import { Stars, StarsInput } from "./Stars";
import { Trash, PencilSimple } from "@phosphor-icons/react";

/** Public plan reviews section. Anonymous users can read; signed-in users can
 *  post/update their own review (one per plan). Plan author cannot review. */
export default function PlanReviewsSection({ slug, planOwnerId, currentUser }) {
  const [data, setData] = useState(null);  // {avg_rating, review_count, items, my_review}
  const [err, setErr] = useState(null);
  const [draftRating, setDraftRating] = useState(0);
  const [draftBody, setDraftBody] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const isOwner = useMemo(
    () => !!(currentUser && planOwnerId && currentUser.id === planOwnerId),
    [currentUser, planOwnerId],
  );

  const load = async () => {
    try {
      const { data } = await api.get(`/public/plans/${slug}/reviews`);
      setData(data);
      if (data.my_review) {
        setDraftRating(data.my_review.rating);
        setDraftBody(data.my_review.body || "");
      }
    } catch (e) {
      setErr(extractApiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!draftRating) { setErr("Please pick at least 1 star."); return; }
    setSaving(true);
    setErr(null);
    try {
      await api.post(`/public/plans/${slug}/reviews`, {
        rating: draftRating,
        body: draftBody.trim() || null,
      });
      setEditing(false);
      await load();
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete your review?")) return;
    setSaving(true);
    try {
      await api.delete(`/public/plans/${slug}/reviews/me`);
      setDraftRating(0);
      setDraftBody("");
      setEditing(false);
      await load();
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <section className="mt-10 border-t border-border pt-8" data-testid="reviews-loading">
        <div className="label-mono animate-pulse">LOADING REVIEWS...</div>
      </section>
    );
  }

  const myReview = data.my_review;
  const otherReviews = data.items.filter((r) => !myReview || r.id !== myReview.id);

  return (
    <section className="mt-10 border-t border-border pt-8" data-testid="reviews-section">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="label-mono text-volt mb-1">// OPERATOR REVIEWS</div>
          <h2 className="font-display text-3xl font-black tracking-tight">
            {data.avg_rating > 0 ? data.avg_rating.toFixed(1) : "—"}
            <span className="text-base text-muted-foreground font-normal ml-2">/ 5</span>
          </h2>
          <div className="mt-2 flex items-center gap-2">
            <Stars value={data.avg_rating} size={16} data-testid="reviews-avg-stars" />
            <span className="text-xs text-muted-foreground" data-testid="reviews-count">
              {data.review_count} {data.review_count === 1 ? "review" : "reviews"}
            </span>
          </div>
        </div>
      </div>

      {/* My review (sign-in gated, not for plan author) */}
      {!currentUser ? (
        <div className="border border-dashed border-border p-5 mb-6 text-sm text-muted-foreground" data-testid="reviews-anon-cta">
          <Link to="/login" className="text-volt hover:underline">Sign in</Link> to leave a review.
        </div>
      ) : isOwner ? (
        <div className="border border-dashed border-border p-5 mb-6 text-sm text-muted-foreground" data-testid="reviews-owner-notice">
          You authored this plan — operators who copy and run it can leave reviews here.
        </div>
      ) : (
        <div className="bg-card border border-border p-5 mb-6" data-testid="my-review-card">
          {myReview && !editing ? (
            <>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="label-mono text-volt">// YOUR REVIEW</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditing(true)} className="text-xs text-muted-foreground hover:text-volt inline-flex items-center gap-1" data-testid="my-review-edit">
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                  <button onClick={remove} disabled={saving} className="text-xs text-muted-foreground hover:text-red-400 inline-flex items-center gap-1 disabled:opacity-50" data-testid="my-review-delete">
                    <Trash size={12} weight="bold" /> Delete
                  </button>
                </div>
              </div>
              <Stars value={myReview.rating} size={16} />
              {myReview.body && (
                <p className="text-sm text-foreground/90 mt-2 whitespace-pre-wrap" data-testid="my-review-body">{myReview.body}</p>
              )}
            </>
          ) : (
            <form onSubmit={submit}>
              <div className="label-mono text-volt mb-2">// {myReview ? "EDIT YOUR REVIEW" : "RATE THIS PLAN"}</div>
              <StarsInput value={draftRating} onChange={setDraftRating} />
              <textarea
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                placeholder="How did this plan run for you? Honest feedback helps the community."
                rows={3}
                maxLength={1500}
                className="mt-3 w-full bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-volt resize-none"
                data-testid="my-review-textarea"
              />
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{draftBody.length}/1500</span>
                <div className="flex items-center gap-2">
                  {editing && myReview && (
                    <button type="button" onClick={() => { setEditing(false); setDraftRating(myReview.rating); setDraftBody(myReview.body || ""); }} className="border border-border px-3 py-1.5 uppercase tracking-widest hover:border-volt" data-testid="my-review-cancel">
                      Cancel
                    </button>
                  )}
                  <button type="submit" disabled={saving || !draftRating} className="bg-volt text-black px-4 py-1.5 uppercase tracking-widest font-display disabled:opacity-40 hover:bg-white" data-testid="my-review-submit">
                    {saving ? "Saving…" : myReview ? "Update" : "Submit"}
                  </button>
                </div>
              </div>
            </form>
          )}
          {err && <div className="text-red-400 text-xs mt-3" data-testid="my-review-error">{err}</div>}
        </div>
      )}

      {/* Other reviews */}
      {otherReviews.length === 0 && !myReview ? (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="reviews-empty">
          No reviews yet — be the first to leave one.
        </div>
      ) : (
        <div className="space-y-4" data-testid="reviews-list">
          {otherReviews.map((r) => (
            <div key={r.id} className="bg-card border border-border p-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-display uppercase tracking-widest text-sm" data-testid={`review-author-${r.id}`}>{r.user_name}</div>
                <Stars value={r.rating} size={12} data-testid={`review-stars-${r.id}`} />
              </div>
              <div className="text-[10px] text-muted-foreground mb-2">
                {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
              </div>
              {r.body && <p className="text-sm text-foreground/90 whitespace-pre-wrap" data-testid={`review-body-${r.id}`}>{r.body}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
